import { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * Button — primitive aksi. Primary (Rose Velvet) hanya untuk aksi utama
 * (Bayar, konfirmasi). One Voice Rule: jangan tebar primary sebagai dekorasi.
 * State lengkap: default/hover/focus/active/disabled/loading.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "font-medium rounded-md border transition-[background-color,border-color,color,box-shadow]",
    "duration-150 ease-[var(--ease-out-quint)]",
    "focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-ink border-transparent hover:bg-primary-hover",
        secondary:
          "bg-surface text-ink border-border hover:bg-faint hover:border-border-strong",
        ghost:
          "bg-transparent text-ink border-transparent hover:bg-faint",
        danger:
          "bg-danger text-danger-ink border-transparent hover:brightness-95",
        outline:
          "bg-transparent text-primary border-primary hover:bg-primary-soft",
      },
      size: {
        sm: "h-8 px-3 text-[0.8125rem]",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-5 text-[0.9375rem]",
        // xl — zona jempol mobile / numpad, tap target nyaman
        xl: "h-14 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "secondary", size: "md", block: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, block, asChild, loading, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, block }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-variant={variant ?? "secondary"}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            <span>{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
